import { useEffect, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { activeNetworkSelector } from 'redux/selectors';
import { useLazyGetFindemSsoUrlQuery } from 'services/findemV2';
import { contactsApi } from 'services/contacts';
import { listCreated, listDeleted, setBaseCount, selectBaseCount } from 'features/findemSync';
import history from 'createHistory';
import { buildIframeUrl } from './utils';

const useIframeMessage = (appName) => {
  const appUrls = {
    campaigns: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/crm?tab=campaigns`,
    talentCommunities: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/crm?tab=communities`,
    projects: `${import.meta.env.VITE_FINDEM_APP_NEXT_BASE_URL}/projects?features=guidedSearch`,
    companyResearch: `${import.meta.env.VITE_FINDEM_EXEC_BASE_URL}/my-apps/company-builder?hideLayoutBars=true`,
  };
  const baseUrl = appUrls[appName];

  if (!baseUrl) {
    throw new Error(`Unknown app name: ${appName}. Valid options: ${Object.keys(appUrls).join(', ')}`);
  }

  const parsedBaseUrl = new URL(baseUrl);
  const { origin } = parsedBaseUrl;
  const baseSearchParams = parsedBaseUrl.search;

  const activeNetwork = useSelector(activeNetworkSelector);
  const [triggerGetFindemSsoUrl] = useLazyGetFindemSsoUrlQuery();
  const dispatch = useDispatch();
  const store = useStore();
  const [iframeUrl, setIframeUrl] = useState(null);
  const [error, setError] = useState(null);
  const networkSlug = activeNetwork?.slug;

  // Capture the shared-list count from the RTK Query cache before dispatching
  // listCreated/listDeleted so the polling baseline is set before the backend
  // sync can complete (prevents a race where the first poll captures a
  // post-sync count, making the expected-delta comparison always fail).
  const captureBaseCount = () => {
    const state = store.getState();
    if (selectBaseCount(state) !== null) return;
    const cached = contactsApi.endpoints.getSharedLists.select({ countOnly: true })(state);
    const count = cached?.data?.meta?.sharedListTotal;
    if (count != null) {
      dispatch(setBaseCount(count));
    }
  };

  const buildRedirectUri = () => {
    const urlParam = new URLSearchParams(window.location.search).get('url');
    if (urlParam) {
      const constructed = buildIframeUrl(origin, urlParam, baseSearchParams);
      if (constructed) return constructed;
    }
    return baseUrl;
  };

  useEffect(() => {
    if (!activeNetwork?.id) return undefined;

    let cancelled = false;
    const redirectUri = buildRedirectUri();
    const request = triggerGetFindemSsoUrl({
      collectionId: activeNetwork.id,
      redirectUri,
    });

    request.then(({ data, error: fetchError }) => {
      if (cancelled) return;

      if (data?.ssoUrl) {
        setIframeUrl(data.ssoUrl);
      } else if (fetchError) {
        setError(fetchError);
      }
    });

    return () => {
      cancelled = true;
      request.abort();
    };
  }, [activeNetwork?.id, appName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== origin) return;
      const { type, action } = event.data || {};

      if (type === 'navigate') {
        if (action === 'view:network-contacts' && networkSlug) {
          const { data } = event.data;
          if (data?.getroListId) {
            history.push(`/networks/${networkSlug}/list/${data.getroListId}/contacts`);
          } else {
            history.push(`/networks/${networkSlug}/lists`);
          }
        }
        return;
      }

      if (type === 'companyBuilder') {
        if (action === 'project:created') {
          captureBaseCount();
          dispatch(listCreated());
        } else if (action === 'project:deleted') {
          captureBaseCount();
          dispatch(listDeleted());
        }
        return;
      }

      if (type === 'talentPool') {
        if (action === 'list.created') {
          captureBaseCount();
          dispatch(listCreated());
        } else if (action === 'list.deleted') {
          captureBaseCount();
          dispatch(listDeleted());
        }
        return;
      }

      // Update parent URL bar
      if (type === 'searchPageMsg' || type === 'searchesPageMsg') {
        const { pathName } = event.data;
        if (pathName) {
          const url = new URL(`${window.location.origin}${window.location.pathname}`);
          url.searchParams.set('url', pathName);
          window.history.pushState({}, '', url.toString());
        }
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [origin, dispatch, networkSlug]);

  return { iframeUrl, error };
};

export default useIframeMessage;
